import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useRedirectUrl } from "../../../../../hooks/Authorize";
import { setCookie } from "../../../../../utilities/cookies";
import { buildAuthenticatedDestination, clearRememberedRedirectUrl, resolveRedirectUrl } from "../../../../../utilities/authRedirect";
import GatewayShell from "../../../../../gateway/GatewayShell";
import { getDestinationContext } from "../../../../../gateway/destinationEngine";
import GatewayIdentityCard from "../../../../../gateway/GatewayIdentityCard";
import { clearRememberedIdentity, getRememberedIdentity, normalizeIdentity, rememberIdentity } from "../../../../../gateway/gatewayIdentity";
import GatewayAction from "../../../../../gateway/GatewayAction";
import GatewayField from "../../../../../gateway/GatewayField";
import GatewayNotice from "../../../../../gateway/GatewayNotice";
import { savePendingAuthentication } from "../../../../../gateway/pendingAuthentication";
import { clearIdentityTransaction, getIdentityTransaction, updateIdentityTransaction } from "../../../../../gateway/identityTransaction";
import { getRealtimeSessionId } from "../../../../../utilities/realtimeSession";

const Login = () => {
  const redirectUrl = useRedirectUrl();
  const navigate = useNavigate();
  const rememberedForm = JSON.parse(localStorage.getItem("formData") || "{}");
  const rememberedProfile = useMemo(() => getRememberedIdentity(), []);
  const identity = rememberedProfile || normalizeIdentity({}, rememberedForm.email ? `${rememberedForm.email}@clikkmail.com` : "");
  const transaction = getIdentityTransaction() || { clientId: "accounts", protocolVersion: "2", redirectUri: "", referralApp: "", internalReturnPath: "" };
  const referralApp = transaction.referralApp || "";
  const destination = getDestinationContext({ redirectUrl, referralApp });
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formik = useFormik({
    initialValues: { password: "" },
    validationSchema: Yup.object({ password: Yup.string().required("Enter your password") }),
    onSubmit: async (values) => {
      if (!rememberedForm.email) return navigate("/login", { replace: true });
      setSubmitting(true); setErrorMessage("");
      const formData = new FormData();
      formData.append("password", values.password);
      formData.append("email", `${rememberedForm.email}@clikkmail.com`);
      formData.append("login_app", destination.product);
      formData.append("client_id", transaction.clientId);
      formData.append("identity_protocol_version", transaction.protocolVersion);
      const realtimeSessionId = getRealtimeSessionId();
      if (realtimeSessionId) formData.append("realtime_session_id", realtimeSessionId);
      const registeredRedirectUri = transaction.redirectUri || "";
      if (registeredRedirectUri) formData.append("redirect_uri", registeredRedirectUri);
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/auth/login`, { method: "POST", body: formData });
        const responseData = await response.json();
        if (!responseData.success) throw new Error(responseData.message || responseData.error || "Your password could not be verified.");
        rememberIdentity(responseData.user, `${rememberedForm.email}@clikkmail.com`);
        if (responseData.security?.verificationRequired) {
          savePendingAuthentication(responseData);
          navigate("/security-verification");
          return;
        }
        setCookie("user", responseData);
        const postLoginPath = transaction.internalReturnPath;
        const resolvedRedirect = resolveRedirectUrl(redirectUrl);
        clearRememberedRedirectUrl();
        document.cookie = "redirectto=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;";
        if (postLoginPath?.startsWith("/")) {
          updateIdentityTransaction({ internalReturnPath: "" });
          navigate(postLoginPath, { replace: true });
          return;
        }
        if (!resolvedRedirect) {
          const authenticatedDestination = buildAuthenticatedDestination({ userId: responseData.user?._id, refreshToken: responseData.refreshToken, security: responseData.security });
          clearIdentityTransaction();
          window.location.replace(authenticatedDestination);
          return;
        }
        const authenticatedDestination = buildAuthenticatedDestination({ redirectUrl: resolvedRedirect, userId: responseData.user?._id, refreshToken: responseData.refreshToken, security: responseData.security });
        clearIdentityTransaction();
        window.location.replace(authenticatedDestination);
      } catch (error) {
        setErrorMessage(error.message); toast.error(error.message);
      } finally { setSubmitting(false); }
    },
  });

  return (
    <GatewayShell stage="password-verification" destinationLabel={destination.continuingLabel} onDismiss={() => navigate("/login")}>
      <form className="gatewayCard" onSubmit={formik.handleSubmit} noValidate>
        <div className="gatewayCard__icon" aria-hidden="true">◇</div>
        <h2>Identity recognized</h2>
        <p className="gatewayCard__sub">Enter your password to finish secure verification.</p>
        <GatewayIdentityCard identity={identity} compact />
        <GatewayNotice title="Password protected">Additional verification appears only when a real organization policy and supported API method require it.</GatewayNotice>
        <GatewayField label="Password" revealPassword autoFocus type="password" autoComplete="current-password" name="password" value={formik.values.password} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder="Password" error={(formik.touched.password && formik.errors.password) || errorMessage} />
        <GatewayAction type="submit" disabled={submitting}>{submitting ? "Verifying securely…" : "Enter Clikkle"}</GatewayAction>
        <GatewayAction variant="secondary" type="button" onClick={() => { clearRememberedIdentity(); navigate("/login"); }}>Use another account</GatewayAction>
        <div className="gatewayActions"><Link to="/account-recovery-forgot-password">Forgot password?</Link><span className="gatewayLink" title="Passkeys require the future Accounts API WebAuthn contract">Passkeys — planned</span></div>
      </form>
    </GatewayShell>
  );
};
export default Login;
